import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * A passive pill-shaped metadata label. It never receives pointer handlers.
 */
export const badgeVariants = cva(
  "inline-flex min-h-5 items-center gap-1 whitespace-nowrap rounded-pill font-sans text-[11px] font-semibold tracking-[0.01em] [&_svg]:size-3",
  {
    variants: {
      tone: {
        outline: "bg-canvas-soft text-ink-soft",
        soft: "bg-canvas-soft text-ink-soft",
        ink: "bg-ink text-paper",
        accent: "bg-accent-subtle font-semibold text-accent-text",
        brand: "bg-action-bg text-white",
        image: "bg-[var(--badge-image)] text-[var(--media-image-on)]",
        video: "bg-[var(--badge-video)] text-[var(--media-video-on)]",
        voice: "bg-[var(--badge-voice)] text-[var(--media-voice-on)]",
        text: "bg-[var(--badge-text)] text-[var(--media-text-on)]",
        asset: "bg-[var(--badge-asset)] text-[var(--media-asset-on)]",
        violet: "bg-[var(--badge-violet-bg)] text-[var(--badge-violet-ink)]",
        amber: "bg-[var(--badge-amber)] text-[var(--warning-on)]",
        teal: "bg-success-subtle text-success-text",
        danger: "bg-[var(--badge-danger)] text-white",
      },
      size: {
        xs: "px-[9px] py-1",
        sm: "px-2.5 py-1 text-xs",
      },
    },
    defaultVariants: { tone: "outline", size: "xs" },
  },
);

export interface BadgeProps
  extends
    ComponentPropsWithoutRef<"span">,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, size, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone, size }), className)} {...props} />
  );
}
